import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

async function getUserPrimaryAccountId(userId: string): Promise<string | null> {
  const { data } = await supabaseAdmin
    .from("account_members")
    .select("account_id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();
  return data?.account_id ?? null;
}

async function resolveWorkspaceIds(accountId: string, lensId?: string | null): Promise<string[]> {
  if (lensId) {
    const { data: lens } = await supabaseAdmin
      .from("lenses")
      .select("workspace_ids, account_id")
      .eq("id", lensId)
      .maybeSingle();
    if (lens && lens.account_id === accountId && Array.isArray(lens.workspace_ids)) {
      return lens.workspace_ids as string[];
    }
  }
  const { data: ws } = await supabaseAdmin
    .from("workspaces")
    .select("id")
    .eq("account_id", accountId);
  return (ws ?? []).map((w: any) => w.id);
}

// ============================================================
// getCommandCenter, hero tiles + program breakdown + mrr series + top customers
// ============================================================
const CommandCenterInput = z.object({
  lens_id: z.string().uuid().nullable().optional(),
  from: z.string(),
  to: z.string(),
});

export const getCommandCenter = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => CommandCenterInput.parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const accountId = await getUserPrimaryAccountId(userId);
    if (!accountId) return { metrics: null, workspace_ids: [], account_id: null };

    const wsIds = await resolveWorkspaceIds(accountId, data.lens_id);
    if (wsIds.length === 0) {
      return { metrics: null, workspace_ids: [], account_id: accountId };
    }

    const { data: metrics, error } = await supabaseAdmin.rpc(
      "account_command_center_metrics",
      {
        _account_id: accountId,
        _workspace_ids: wsIds,
        _from: data.from,
        _to: data.to,
      },
    );
    if (error) throw new Error(error.message);
    return { metrics, workspace_ids: wsIds, account_id: accountId };
  });

// ============================================================
// getActivityFeed, cross-workspace activity with cursor pagination
// ============================================================
const ActivityFeedInput = z.object({
  lens_id: z.string().uuid().nullable().optional(),
  filter: z.enum(["all", "wins", "churns", "anomalies", "team"]).default("all"),
  cursor: z.object({ occurred_at: z.string(), id: z.string().uuid() }).nullable().optional(),
  limit: z.number().int().min(1).max(100).default(25),
});

const FILTER_EVENT_TYPES: Record<string, string[] | null> = {
  all: null,
  wins: ["commission_earned", "contact_added", "status_changed"],
  churns: ["churn_detected", "cross_program_churn_alert"],
  anomalies: ["anomaly_flagged"],
  team: ["team_action", "workspace_created"],
};

export const getActivityFeed = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ActivityFeedInput.parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const accountId = await getUserPrimaryAccountId(userId);
    if (!accountId) return { events: [], next_cursor: null };

    const wsIds = await resolveWorkspaceIds(accountId, data.lens_id);

    let q = supabaseAdmin
      .from("activity_events")
      .select("id, event_type, description, metadata_json, occurred_at, workspace_id, contact_id")
      .eq("account_id", accountId)
      .order("occurred_at", { ascending: false })
      .order("id", { ascending: false })
      .limit(data.limit + 1);

    if (wsIds.length > 0) {
      q = q.or(`workspace_id.is.null,workspace_id.in.(${wsIds.join(",")})`);
    }

    const types = FILTER_EVENT_TYPES[data.filter];
    if (types) q = q.in("event_type", types);

    if (data.cursor) {
      q = q.or(
        `occurred_at.lt.${data.cursor.occurred_at},and(occurred_at.eq.${data.cursor.occurred_at},id.lt.${data.cursor.id})`,
      );
    }

    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    const hasMore = (rows?.length ?? 0) > data.limit;
    const events = (rows ?? []).slice(0, data.limit);
    const next_cursor = hasMore && events.length
      ? { occurred_at: events[events.length - 1].occurred_at, id: events[events.length - 1].id }
      : null;

    // attach workspace program names
    const wsMap = new Map<string, string>();
    if (events.length) {
      const uniqWs = [...new Set(events.map((e: any) => e.workspace_id).filter(Boolean))];
      if (uniqWs.length) {
        const { data: ws } = await supabaseAdmin
          .from("workspaces")
          .select("id, program_name")
          .in("id", uniqWs);
        (ws ?? []).forEach((w: any) => wsMap.set(w.id, w.program_name));
      }
    }

    return {
      events: events.map((e: any) => ({ ...e, program_name: e.workspace_id ? wsMap.get(e.workspace_id) ?? null : null })),
      next_cursor,
    };
  });

// ============================================================
// getMyAccountPlan, plan gating source of truth
// ============================================================
export const getMyAccountPlan = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const accountId = await getUserPrimaryAccountId(userId);
    if (!accountId) return null;
    const { data } = await supabaseAdmin
      .from("accounts")
      .select("id, plan, plan_status, trial_ends_at, is_admin_comped")
      .eq("id", accountId)
      .maybeSingle();
    return data;
  });

// ============================================================
// Lenses
// ============================================================
export const listMyLenses = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const accountId = await getUserPrimaryAccountId(userId);
    if (!accountId) return { lenses: [] };
    const { data } = await supabaseAdmin
      .from("lenses")
      .select("id, name, workspace_ids, is_default, filter_config_json, created_at")
      .eq("account_id", accountId)
      .order("created_at", { ascending: false });
    return { lenses: data ?? [] };
  });

const CreateLensInput = z.object({
  name: z.string().min(1).max(120),
  workspace_ids: z.array(z.string().uuid()).min(1),
  filter_config_json: z.record(z.string(), z.any()).default({}),
  is_default: z.boolean().default(false),
});

export const createLens = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => CreateLensInput.parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const accountId = await getUserPrimaryAccountId(userId);
    if (!accountId) throw new Error("No account");
    if (data.is_default) {
      await supabaseAdmin.from("lenses").update({ is_default: false }).eq("account_id", accountId);
    }
    const { data: lens, error } = await supabaseAdmin
      .from("lenses")
      .insert({
        account_id: accountId,
        name: data.name,
        workspace_ids: data.workspace_ids,
        filter_config_json: data.filter_config_json,
        is_default: data.is_default,
        created_by_user_id: userId,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { lens_id: lens!.id };
  });

export const deleteLens = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ lens_id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const accountId = await getUserPrimaryAccountId(userId);
    if (!accountId) throw new Error("No account");
    await supabaseAdmin.from("lenses").delete().eq("id", data.lens_id).eq("account_id", accountId);
    return { ok: true };
  });

// ============================================================
// listGlobalPeople, deduplicated cross-program contacts
// ============================================================
const PeopleInput = z.object({
  search: z.string().max(200).optional().default(""),
  sort: z.enum(["ltv", "recent", "name"]).default("ltv"),
  limit: z.number().int().min(1).max(200).default(50),
  offset: z.number().int().min(0).default(0),
});

export const listGlobalPeople = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => PeopleInput.parse(d))
  .handler(async ({ data, context }) => {
    const accountId = await getUserPrimaryAccountId(context.userId);
    if (!accountId) return { people: [], total: 0 };

    let q = supabaseAdmin
      .from("global_contacts")
      .select(
        "id, primary_email, all_emails, merged_name, merged_phone, total_ltv, first_seen_at, last_activity_at, churn_risk_score, churn_risk_explanation",
        { count: "exact" },
      )
      .eq("account_id", accountId);

    if (data.search) {
      const term = `%${data.search.replace(/[%_]/g, "")}%`;
      q = q.or(`primary_email.ilike.${term},merged_name.ilike.${term}`);
    }

    if (data.sort === "ltv") q = q.order("total_ltv", { ascending: false });
    else if (data.sort === "recent") q = q.order("last_activity_at", { ascending: false, nullsFirst: false });
    else q = q.order("merged_name", { ascending: true });

    q = q.range(data.offset, data.offset + data.limit - 1);

    const { data: rows, error, count } = await q;
    if (error) throw new Error(error.message);

    // Attach program counts via global_contact_links
    const ids = (rows ?? []).map((r: any) => r.id);
    const links: Record<string, { workspace_id: string; program_name: string }[]> = {};
    if (ids.length) {
      const { data: linkRows } = await supabaseAdmin
        .from("global_contact_links")
        .select("global_contact_id, contacts!inner(workspace_id, workspaces!inner(program_name))")
        .in("global_contact_id", ids);
      (linkRows ?? []).forEach((l: any) => {
        const arr = links[l.global_contact_id] ?? (links[l.global_contact_id] = []);
        const ws = l.contacts?.workspace_id;
        const name = l.contacts?.workspaces?.program_name;
        if (ws && !arr.find((x) => x.workspace_id === ws)) arr.push({ workspace_id: ws, program_name: name });
      });
    }

    return {
      people: (rows ?? []).map((r: any) => ({ ...r, programs: links[r.id] ?? [] })),
      total: count ?? 0,
    };
  });

// ============================================================
// getProgramComparison, side-by-side workspace metrics
// ============================================================
const CompareInput = z.object({
  from: z.string(),
  to: z.string(),
  workspace_ids: z.array(z.string().uuid()).optional(),
});

export const getProgramComparison = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => CompareInput.parse(d))
  .handler(async ({ data, context }) => {
    const accountId = await getUserPrimaryAccountId(context.userId);
    if (!accountId) return { programs: [] };

    const wsIds = data.workspace_ids?.length
      ? data.workspace_ids
      : await resolveWorkspaceIds(accountId, null);
    if (!wsIds.length) return { programs: [] };

    const { data: workspaces } = await supabaseAdmin
      .from("workspaces")
      .select("id, program_name, revenue_model")
      .in("id", wsIds);

    const programs: any[] = [];
    for (const ws of workspaces ?? []) {
      try {
        const { data: metrics } = await supabaseAdmin.rpc("workspace_dashboard_metrics_v2", {
          _workspace_id: ws.id,
        } as any);
        programs.push({ workspace_id: ws.id, program_name: ws.program_name, revenue_model: ws.revenue_model, metrics });
      } catch {
        programs.push({ workspace_id: ws.id, program_name: ws.program_name, revenue_model: ws.revenue_model, metrics: null });
      }
    }
    return { programs };
  });

// ============================================================
// explainChurnRisk, AI-generated churn explanation for a global contact
// ============================================================
import { generateText } from "ai";
import { getLovableModel } from "./ai-gateway";

export const explainChurnRisk = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ global_contact_id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const accountId = await getUserPrimaryAccountId(context.userId);
    if (!accountId) throw new Error("No account");

    const { data: gc } = await supabaseAdmin
      .from("global_contacts")
      .select("id, primary_email, merged_name, total_ltv, first_seen_at, last_activity_at, account_id")
      .eq("id", data.global_contact_id)
      .maybeSingle();
    if (!gc || gc.account_id !== accountId) throw new Error("Not found");

    // Pull linked contacts with status across programs
    const { data: links } = await supabaseAdmin
      .from("global_contact_links")
      .select("contacts!inner(status, tier, commission_amount, date_status_changed, workspaces!inner(program_name, revenue_model))")
      .eq("global_contact_id", data.global_contact_id);

    const programs = (links ?? []).map((l: any) => ({
      program: l.contacts?.workspaces?.program_name,
      revenue_model: l.contacts?.workspaces?.revenue_model,
      status: l.contacts?.status,
      tier: l.contacts?.tier,
      amount: l.contacts?.commission_amount,
      changed: l.contacts?.date_status_changed,
    }));

    const recurring = programs.filter((p) => p.revenue_model === "recurring");
    const churned = recurring.filter((p) => /churn|cancel|refund|past[ _-]?due/i.test(p.status ?? ""));
    const active = recurring.filter((p) => !/churn|cancel|refund/i.test(p.status ?? ""));
    let score = 0;
    if (recurring.length > 0) score = churned.length / recurring.length;
    if (gc.last_activity_at) {
      const days = (Date.now() - new Date(gc.last_activity_at).getTime()) / 86_400_000;
      if (days > 90) score = Math.min(1, score + 0.2);
    }

    let explanation = "";
    try {
      const model = getLovableModel("google/gemini-3-flash-preview");
      const { text } = await generateText({
        model,
        prompt: `You are an affiliate-marketing CRM analyst. In 2-3 short sentences, explain this customer's churn risk in plain English. Be concrete about which programs they're churning vs. still active in.\n\nCustomer: ${gc.merged_name ?? gc.primary_email}\nTotal LTV: $${gc.total_ltv}\nLast activity: ${gc.last_activity_at ?? "unknown"}\nPrograms: ${JSON.stringify(programs)}\nChurned in: ${churned.length}/${recurring.length} recurring programs.`,
      });
      explanation = text.trim();
    } catch (e: any) {
      explanation = churned.length > 0
        ? `Churned in ${churned.length} of ${recurring.length} recurring program${recurring.length === 1 ? "" : "s"}, still active in ${active.length}.`
        : "Stable across all active programs.";
    }

    await supabaseAdmin
      .from("global_contacts")
      .update({ churn_risk_score: score, churn_risk_explanation: explanation })
      .eq("id", data.global_contact_id);

    return { score, explanation };
  });

// ============================================================
// mergeGlobalContacts, manually merge two global contacts
// ============================================================
export const mergeGlobalContacts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ keep_id: z.string().uuid(), merge_id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const accountId = await getUserPrimaryAccountId(context.userId);
    if (!accountId) throw new Error("No account");
    if (data.keep_id === data.merge_id) throw new Error("Cannot merge into self");

    const { data: both } = await supabaseAdmin
      .from("global_contacts")
      .select("*")
      .in("id", [data.keep_id, data.merge_id]);
    const keep = both?.find((b: any) => b.id === data.keep_id);
    const merge = both?.find((b: any) => b.id === data.merge_id);
    if (!keep || !merge) throw new Error("Contact not found");
    if (keep.account_id !== accountId || merge.account_id !== accountId)
      throw new Error("Unauthorized");

    // Move links
    await supabaseAdmin
      .from("global_contact_links")
      .update({ global_contact_id: data.keep_id })
      .eq("global_contact_id", data.merge_id);

    // Merge metadata
    const emails = Array.from(new Set([...(keep.all_emails ?? []), ...(merge.all_emails ?? [])]));
    const prevHistory = Array.isArray(keep.merge_history) ? (keep.merge_history as any[]) : [];
    const history = [
      ...prevHistory,
      { merged_from: data.merge_id, merged_at: new Date().toISOString(), by_user: context.userId, snapshot: { primary_email: merge.primary_email, name: merge.merged_name, ltv: merge.total_ltv } },
    ];

    await supabaseAdmin
      .from("global_contacts")
      .update({
        all_emails: emails,
        total_ltv: Number(keep.total_ltv ?? 0) + Number(merge.total_ltv ?? 0),
        merged_name: keep.merged_name ?? merge.merged_name,
        merged_phone: keep.merged_phone ?? merge.merged_phone,
        first_seen_at: keep.first_seen_at && merge.first_seen_at
          ? (keep.first_seen_at < merge.first_seen_at ? keep.first_seen_at : merge.first_seen_at)
          : (keep.first_seen_at ?? merge.first_seen_at),
        last_activity_at: keep.last_activity_at && merge.last_activity_at
          ? (keep.last_activity_at > merge.last_activity_at ? keep.last_activity_at : merge.last_activity_at)
          : (keep.last_activity_at ?? merge.last_activity_at),
        merge_history: history,
      })
      .eq("id", data.keep_id);

    await supabaseAdmin.from("global_contacts").delete().eq("id", data.merge_id);
    return { ok: true };
  });

// ============================================================
// splitGlobalContact, unlink one contact from its global profile
// ============================================================
export const splitGlobalContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ global_contact_id: z.string().uuid(), contact_id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const accountId = await getUserPrimaryAccountId(context.userId);
    if (!accountId) throw new Error("No account");

    const { data: gc } = await supabaseAdmin
      .from("global_contacts")
      .select("id, account_id")
      .eq("id", data.global_contact_id)
      .maybeSingle();
    if (!gc || gc.account_id !== accountId) throw new Error("Not found");

    await supabaseAdmin
      .from("global_contact_links")
      .delete()
      .eq("global_contact_id", data.global_contact_id)
      .eq("contact_id", data.contact_id);

    return { ok: true };
  });
